import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";
import { types, flow, isAlive, onSnapshot, getSnapshot, addDisposer } from "mobx-state-tree";
import { observable } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { QueuedFile } from "./models";
import type {
  QueuedFileInstance,
  QueuedFileSnapshotIn,
  QueuedFileSnapshotOut,
  SkippedFile,
  AddFilesSummary,
} from "./models";
import * as uploadRepo from "./repository";
import { ensureFreshToken } from "./repository";
import { computeFileHash } from "@/utils/fileHash";
import { extractEmbeddedDates } from "@/utils/fileMetadata";
import type { EmbeddedFileDates } from "@/utils/fileMetadata";
import {
  isBlockedFileType,
  isIgnoredFile,
  resolveContentType,
} from "@/domains/documents/constants/fileTypes";
import { getTranslationsStatic } from "@/lib/i18n/LanguageContext";
import { capture } from "@/lib/sentry/capture";
import {
  BuildingStorageQuotaError,
  cancelBuildingStorageUpload,
  parseBuildingStorageId,
  reserveBuildingStorageUploads,
} from "@/domains/documents/services/buildingStorageQuota";
import type { StorageReservation } from "@/domains/documents/services/buildingStorageQuota";
import { discardBuildingStorageUpload } from "@/domains/documents/services/discardBuildingStorageUpload";

/** Maximum allowed file size: 500MB */
const MAX_FILE_SIZE = 500 * 1024 * 1024; // 524,288,000 bytes

/**
 * Configuration for adding files to the upload queue.
 */
export interface AddFilesConfig {
  /** The building ID for storage path */
  buildingId: string;
  /** The organization ID for storage path */
  orgId: string;
  /** Optional project ID for document association */
  projectId?: string;
  /** Optional parent folder ID for uploads into a selected folder */
  parentFolderId?: string;
}

/**
 * Upload Queue Store - Manages bulk file uploads with concurrency control.
 *
 * Features:
 * - Controlled concurrency (default 3 simultaneous uploads)
 * - Upload verification (confirms stored size matches original)
 * - Automatic retries with exponential backoff (up to 3 attempts)
 * - Real-time progress tracking with file-level status
 * - File handles stored in volatile (not serialized)
 *
 * Design:
 * - Queue state is observable (MST models)
 * - File handles are volatile (Map<id, File>)
 * - Workers process queue items concurrently
 * - Processing can be paused/resumed via isProcessing flag
 */
/**
 * Give a queued file a fresh storage path for its next attempt.
 * Storage objects are never hard-deleted anymore, so a failed attempt's
 * object (e.g. one that failed verification) stays in the bucket — the
 * retry must upload to a new path because uploads use upsert:false.
 */
function regenerateFilePath(queuedFile: { filePath: string; fileName: string }): void {
  const prefix = queuedFile.filePath.slice(0, queuedFile.filePath.lastIndexOf("/") + 1);
  const dotIndex = queuedFile.fileName.lastIndexOf(".");
  // dotIndex > 0 so extension-less names ("README") and dotfiles
  // (".gitignore") don't get their whole name appended as an "extension"
  const suffix = dotIndex > 0 ? "." + queuedFile.fileName.slice(dotIndex + 1) : "";
  queuedFile.filePath = prefix + uuidv4() + suffix;
}

function releaseHeldReservations(files: QueuedFileInstance[]): void {
  for (const file of files) {
    if (file.registrationUncertain) continue;
    if (!file.reservationId) continue;
    const reservationId = file.reservationId;
    const buildingId = parseBuildingStorageId(file.buildingId);
    const fileId = file.id;
    file.clearReservation();
    if (buildingId === null) {
      capture(new Error(`Invalid building ID: ${file.buildingId}`), {
        tags: { feature: "building-storage-quota", stage: "cancel-upload" },
        fingerprint: ["building-storage-quota", "cancel-upload"],
        context: { quota: { buildingId: file.buildingId, fileId } },
      });
      continue;
    }
    void cancelBuildingStorageUpload(reservationId, buildingId).catch((error) => {
      capture(error instanceof Error ? error : new Error(String(error)), {
        tags: { feature: "building-storage-quota", stage: "cancel-upload" },
        fingerprint: ["building-storage-quota", "cancel-upload"],
        context: { quota: { buildingId, fileId } },
      });
    });
  }
}

function checkedQueueReservations(
  pending: Array<{ snapshot: QueuedFileSnapshotIn }>,
  reservations: StorageReservation[]
): Map<string, StorageReservation> {
  if (reservations.length !== pending.length) {
    throw new Error("Storage reservation batch response did not match selected files");
  }
  const expected = new Map(pending.map(({ snapshot }) => [snapshot.id, snapshot.filePath]));
  const byId = new Map<string, StorageReservation>();
  for (const reservation of reservations) {
    if (
      expected.get(reservation.idempotencyKey) !== reservation.path ||
      byId.has(reservation.idempotencyKey)
    ) {
      throw new Error("Storage reservation batch response did not match selected files");
    }
    byId.set(reservation.idempotencyKey, reservation);
  }
  return byId;
}

async function releaseUnattachedReservations(
  reservations: StorageReservation[],
  buildingId: number
): Promise<void> {
  const ids = new Set(reservations.map((reservation) => reservation.reservationId));
  await Promise.all(
    Array.from(ids, async (reservationId) => {
      try {
        await cancelBuildingStorageUpload(reservationId, buildingId);
      } catch (error) {
        capture(error instanceof Error ? error : new Error(String(error)), {
          tags: { feature: "building-storage-quota", stage: "cancel-upload" },
          fingerprint: ["building-storage-quota", "cancel-upload"],
          context: { quota: { buildingId, reservationId } },
        });
      }
    })
  );
}

export const UploadQueueStore = types
  .model("UploadQueueStore", {
    /** Queue of files waiting to be uploaded or being processed */
    queue: types.array(QueuedFile),
    /** Maximum concurrent uploads */
    concurrency: types.optional(types.number, 3),
    /** Maximum retry attempts per file */
    maxRetries: types.optional(types.number, 3),
    /** Whether the queue processor is running */
    isProcessing: types.optional(types.boolean, false),
  })
  .volatile(() => ({
    /**
     * File handles stored outside MST to avoid serialization issues.
     * Map<queuedFileId, File>
     */
    fileHandles: new Map<string, File>(),
    /** Batch outcomes outlive the dismissible transfer card; never serialized. */
    storageBatchFiles: observable.map<string, QueuedFileSnapshotOut>(),
    /** Number of currently active upload workers */
    activeWorkers: 0,
    /** Dismissed rows held until their in-flight document registration settles. */
    pendingRegistrationCleanup: new Set<string>(),
    /** Flag to signal workers to stop */
    shouldStop: false,
    /**
     * Files that were skipped during pre-upload validation.
     * Tracked separately from queue for user reporting.
     */
    skippedFiles: [] as SkippedFile[],
  }))
  .actions((self) => ({
    afterCreate() {
      const remember = (files: QueuedFileSnapshotOut[]) => {
        const present = new Set(files.map((file) => file.id));
        for (const [id, previous] of self.storageBatchFiles) {
          if (!present.has(id) && !previous.documentCreated && !previous.registrationUncertain)
            self.storageBatchFiles.set(id, { ...previous, status: "failed" });
        }
        for (const file of files) {
          if (file.batchId) self.storageBatchFiles.set(file.id, file);
        }
      };
      remember(getSnapshot(self.queue));
      addDisposer(self, onSnapshot(self.queue, remember));
    },
  }))
  .views((self) => ({
    /**
     * Count of files waiting to be uploaded.
     */
    get pendingCount(): number {
      return self.queue.filter((f) => f.status === "pending" || f.status === "retrying").length;
    },

    /**
     * Count of files currently uploading.
     */
    get uploadingCount(): number {
      return self.queue.filter((f) => f.status === "uploading" || f.status === "verifying").length;
    },

    /**
     * Count of successfully completed uploads.
     */
    get completedCount(): number {
      return self.queue.filter((f) => f.status === "complete").length;
    },

    /**
     * Count of permanently failed uploads (exceeded max retries).
     */
    get failedCount(): number {
      return self.queue.filter((f) => f.status === "failed").length;
    },

    /**
     * Total number of files in the queue.
     */
    get totalCount(): number {
      return self.queue.length;
    },

    /**
     * Overall progress as a percentage (0-100).
     * Weighted by file size for accurate progress representation.
     */
    get overallProgress(): number {
      if (self.queue.length === 0) return 0;

      const totalSize = self.queue.reduce((sum, f) => sum + f.fileSize, 0);
      if (totalSize === 0) return 0;

      const completedSize = self.queue.reduce((sum, f) => {
        if (f.status === "complete") return sum + f.fileSize;
        if (f.status === "failed") return sum + f.fileSize; // Count failed as "processed"
        return sum + (f.fileSize * f.progress) / 100;
      }, 0);

      return Math.round((completedSize / totalSize) * 100);
    },

    /**
     * Get all files that permanently failed.
     */
    get failedFiles(): QueuedFileInstance[] {
      return self.queue.filter((f) => f.status === "failed");
    },

    /**
     * Get all files currently uploading or verifying.
     */
    get uploadingFiles(): QueuedFileInstance[] {
      return self.queue.filter((f) => f.status === "uploading" || f.status === "verifying");
    },

    /**
     * Check if there are files a NEW worker could claim. "retrying" files
     * are excluded: each is owned by the worker sleeping out its backoff,
     * which flips it back to "pending" and picks it up itself — treating
     * them as claimable makes the worker respawn loop spin forever.
     */
    get hasWorkToDo(): boolean {
      return self.queue.some((f) => f.status === "pending");
    },

    /**
     * Check if all files have been processed (complete or failed).
     */
    get isComplete(): boolean {
      return (
        self.queue.length > 0 &&
        self.queue.every((f) => f.status === "complete" || f.status === "failed")
      );
    },

    /**
     * Count of files skipped during pre-upload validation.
     */
    get skippedCount(): number {
      return self.skippedFiles.length;
    },

    /**
     * Count of files skipped because they were empty (0 bytes).
     */
    get skippedEmptyCount(): number {
      return self.skippedFiles.filter((f) => f.reason === "empty").length;
    },

    /**
     * Count of files skipped because they exceeded the 500MB size limit.
     */
    get skippedTooLargeCount(): number {
      return self.skippedFiles.filter((f) => f.reason === "too-large").length;
    },

    /**
     * Count of files skipped because they were unreadable.
     */
    get skippedUnreadableCount(): number {
      return self.skippedFiles.filter((f) => f.reason === "unreadable").length;
    },

    /**
     * Get all skipped files for reporting.
     */
    get skippedFilesList(): SkippedFile[] {
      return self.skippedFiles;
    },
  }))
  // First actions block: Define updateFileProgress so it's available on `self` for callbacks
  .actions((self) => ({
    /**
     * Update upload progress for a file.
     * This is a proper MST action that can be called from external callbacks.
     * Defined in separate .actions() block so it's available on `self` when used in callbacks.
     */
    updateFileProgress(fileId: string, progress: number): void {
      const fileToUpdate = self.queue.find((f) => f.id === fileId);
      if (fileToUpdate) {
        fileToUpdate.progress = progress;
      }
    },
  }))
  // Second actions block: Use self.updateFileProgress in callbacks
  .actions((self) => {
    function deferCleanupUntilRegistration(files: QueuedFileInstance[]): void {
      for (const file of files) self.pendingRegistrationCleanup.add(file.id);
    }

    /**
     * Get the next file ready for upload.
     */
    function getNextPendingFile(): QueuedFileInstance | undefined {
      // "retrying" files are owned by the worker sleeping out their backoff
      // (it flips them back to "pending") — a second worker claiming one
      // would double-upload and mutate filePath under the first's feet.
      return self.queue.find((f) => f.status === "pending");
    }

    /**
     * Calculate exponential backoff delay in milliseconds.
     * Formula: 2^(retryCount-1) * 1000ms
     * - Retry 1: 1 second delay
     * - Retry 2: 2 second delay
     * - Retry 3: 4 second delay
     */
    function getBackoffDelay(retryCount: number): number {
      return Math.pow(2, retryCount - 1) * 1000;
    }

    /**
     * Process a single file upload with retry logic.
     * Includes protection against detached MST nodes when queue is cleared during upload.
     */
    const processFile = flow(function* processFile(fileId: string) {
      const queuedFile = self.queue.find((f) => f.id === fileId);
      if (!queuedFile) return;

      // Guard: Check if node is still alive (not detached from tree by cancelAll/reset)
      // This prevents crashes when trying to access a detached MST node
      if (!isAlive(queuedFile)) {
        console.warn(`[UploadQueueStore] Skipping detached node: ${fileId}`);
        return;
      }

      // Guard: Don't process files that are already in terminal state
      // This handles edge cases where async worker management might pick up
      // a file again due to race conditions after it's already completed/failed
      if (queuedFile.status === "failed" || queuedFile.status === "complete") {
        return;
      }

      const file = self.fileHandles.get(fileId);
      if (!file) {
        // Check if node is still alive before updating
        if (!isAlive(queuedFile)) return;
        releaseHeldReservations([queuedFile]);
        queuedFile.status = "failed";
        queuedFile.error = "File handle not found";
        queuedFile.completedAt = new Date().toISOString();
        return;
      }

      // Mark as uploading - check alive first
      if (!isAlive(queuedFile)) return;
      queuedFile.status = "uploading";
      queuedFile.startedAt = new Date().toISOString();
      queuedFile.progress = 0;

      // Auth retry loop: up to 2 auth-refresh attempts (separate from regular retries)
      const MAX_AUTH_RETRIES = 2;
      let authRetryCount = 0;
      let reservationId: string | null = queuedFile.reservationId;
      let uploadedPath: string | null = null;
      const numericBuildingId = parseBuildingStorageId(queuedFile.buildingId);

      async function releaseReservation(): Promise<void> {
        if (!reservationId) return;
        const id = reservationId;
        reservationId = null;
        if (isAlive(queuedFile)) queuedFile.clearReservation();
        if (numericBuildingId === null) {
          capture(new Error(`Invalid building ID: ${queuedFile.buildingId}`), {
            tags: { feature: "building-storage-quota", stage: "cancel-upload" },
            fingerprint: ["building-storage-quota", "cancel-upload"],
            context: { quota: { buildingId: queuedFile.buildingId, fileId } },
          });
          return;
        }
        try {
          if (uploadedPath) {
            await discardBuildingStorageUpload(uploadedPath, id, numericBuildingId);
            uploadedPath = null;
          } else {
            await cancelBuildingStorageUpload(id, numericBuildingId);
          }
        } catch (error) {
          capture(error instanceof Error ? error : new Error(String(error)), {
            tags: { feature: "building-storage-quota", stage: "cancel-upload" },
            fingerprint: ["building-storage-quota", "cancel-upload"],
            context: { quota: { buildingId: numericBuildingId, fileId } },
          });
        }
      }

      while (authRetryCount <= MAX_AUTH_RETRIES) {
        try {
          if (numericBuildingId === null) {
            throw new Error(`Invalid building ID: ${queuedFile.buildingId}`);
          }
          if (reservationId) {
            const expiresAt = queuedFile.reservationExpiresAt;
            const expiryTime = expiresAt ? Date.parse(expiresAt) : NaN;
            if (!Number.isFinite(expiryTime) || expiryTime <= Date.now()) {
              yield releaseReservation();
              if (!isAlive(queuedFile)) return;
            }
          }
          if (!reservationId) {
            const path = queuedFile.filePath;
            const size = queuedFile.fileSize;
            const reservations: Awaited<ReturnType<typeof reserveBuildingStorageUploads>> =
              yield reserveBuildingStorageUploads(numericBuildingId, [
                {
                  idempotencyKey: uuidv4(),
                  path,
                  size,
                },
              ]);
            reservationId = reservations[0].reservationId;
            if (!isAlive(queuedFile)) {
              yield releaseReservation();
              return;
            }
            queuedFile.reservationId = reservationId;
            queuedFile.reservationExpiresAt = reservations[0].expiresAt ?? null;
          }
          if (!isAlive(queuedFile)) {
            yield releaseReservation();
            return;
          }

          // Upload with TUS for real progress tracking (0-70% range, verification uses 70-100%)
          const uploadPath = queuedFile.filePath;
          const uploadResult: uploadRepo.UploadResult = yield uploadRepo.uploadFileWithProgress(
            file,
            uploadPath,
            "documents",
            (bytesUploaded: number, bytesTotal: number) => {
              // Call the MST action via self to update progress from the external TUS callback
              // Note: updateFileProgress already handles missing files gracefully
              const uploadProgress = Math.round((bytesUploaded / bytesTotal) * 70);
              self.updateFileProgress(fileId, uploadProgress);
            }
          );
          if (uploadResult.success) uploadedPath = uploadPath;

          // Check if node was detached during async upload
          if (!isAlive(queuedFile)) {
            console.warn(`[UploadQueueStore] Node detached during upload: ${fileId}`);
            yield releaseReservation();
            return;
          }

          if (!uploadResult.success) {
            // Check if this is an auth error — handle with token refresh
            if (uploadResult.error === "AUTH_EXPIRED") {
              authRetryCount++;
              if (authRetryCount <= MAX_AUTH_RETRIES) {
                console.warn(
                  `[UploadQueueStore] Auth expired for ${queuedFile.fileName}, force-refreshing token (attempt ${authRetryCount}/${MAX_AUTH_RETRIES})`
                );
                try {
                  // Force a real network refresh — getSession() just returns the same stale token
                  yield ensureFreshToken(true);
                  // Reset progress and retry immediately (no backoff, no retryCount increment)
                  queuedFile.progress = 0;
                  continue; // Retry the upload with fresh token
                } catch {
                  // Token refresh failed — session is truly expired
                  if (!isAlive(queuedFile)) return;
                  queuedFile.status = "failed";
                  queuedFile.error = "Session expired. Please sign in again.";
                  queuedFile.completedAt = new Date().toISOString();
                  yield releaseReservation();
                  return;
                }
              } else {
                // Exhausted auth retries
                if (!isAlive(queuedFile)) return;
                queuedFile.status = "failed";
                queuedFile.error = "Session expired. Please sign in again.";
                queuedFile.completedAt = new Date().toISOString();
                yield releaseReservation();
                return;
              }
            }
            if (uploadResult.errorCode === "STORAGE_ADMISSION") {
              throw new BuildingStorageQuotaError(
                uploadResult.error || "Storage admission rejected"
              );
            }
            throw new Error(uploadResult.error || "Upload failed");
          }

          // Verify the upload
          queuedFile.status = "verifying";
          queuedFile.progress = 70;

          const verifyResult: uploadRepo.VerificationResult = yield uploadRepo.verifyUpload(
            queuedFile.filePath,
            queuedFile.fileSize
          );

          // Check if node was detached during async verification
          if (!isAlive(queuedFile)) {
            console.warn(`[UploadQueueStore] Node detached during verification: ${fileId}`);
            yield releaseReservation();
            return;
          }

          if (!verifyResult.verified) {
            // The corrupted object is left in the bucket (storage objects
            // are never hard-deleted); the retry uploads to a fresh path.
            // No row cleanup needed: the documents row is only created by
            // create_document_with_versioning AFTER successful verification.
            throw new Error(verifyResult.error || "Verification failed");
          }

          // Success
          queuedFile.status = "complete";
          queuedFile.progress = 100;
          queuedFile.completedAt = new Date().toISOString();
          queuedFile.error = null;

          // Note: document.uploaded event is logged in DocumentStore when the document
          // reaches COMPLETE state via realtime subscription

          // Clean up file handle
          self.fileHandles.delete(fileId);
          return; // Success — exit the auth retry loop
        } catch (e) {
          // Check if node was detached - if so, just exit silently
          if (!isAlive(queuedFile)) {
            console.warn(`[UploadQueueStore] Node detached during error handling: ${fileId}`);
            yield releaseReservation();
            return;
          }

          const rawErrorMsg = e instanceof Error ? e.message : String(e);
          if (e instanceof BuildingStorageQuotaError) {
            yield releaseReservation();
            queuedFile.status = "failed";
            queuedFile.error = rawErrorMsg;
            queuedFile.completedAt = new Date().toISOString();
            return;
          }

          // Check for auth errors thrown as exceptions
          if (rawErrorMsg === "AUTH_EXPIRED") {
            authRetryCount++;
            if (authRetryCount <= MAX_AUTH_RETRIES) {
              console.warn(
                `[UploadQueueStore] Auth expired for ${queuedFile.fileName}, force-refreshing token (attempt ${authRetryCount}/${MAX_AUTH_RETRIES})`
              );
              try {
                // Force a real network refresh — getSession() just returns the same stale token
                yield ensureFreshToken(true);
                queuedFile.progress = 0;
                continue; // Retry with fresh token
              } catch {
                if (!isAlive(queuedFile)) return;
                queuedFile.status = "failed";
                queuedFile.error = "Session expired. Please sign in again.";
                queuedFile.completedAt = new Date().toISOString();
                yield releaseReservation();
                return;
              }
            } else {
              if (!isAlive(queuedFile)) return;
              queuedFile.status = "failed";
              queuedFile.error = "Session expired. Please sign in again.";
              queuedFile.completedAt = new Date().toISOString();
              yield releaseReservation();
              return;
            }
          }

          // Non-auth error: use standard retry logic with backoff
          yield releaseReservation();
          // Parse upload errors into user-friendly, translated messages
          const t = getTranslationsStatic();
          const errorMsg = rawErrorMsg.includes("413")
            ? t.documents.uploadErrors.fileTooLarge
            : rawErrorMsg;

          if (queuedFile.retryCount < self.maxRetries) {
            // Schedule retry
            queuedFile.status = "retrying";
            queuedFile.retryCount += 1;
            queuedFile.error = `Retry ${queuedFile.retryCount}/${self.maxRetries}: ${errorMsg}`;

            // Wait for backoff delay before allowing retry
            const delay = getBackoffDelay(queuedFile.retryCount);
            yield new Promise((resolve) => setTimeout(resolve, delay));

            // Check again after delay - node may have been detached
            if (!isAlive(queuedFile)) {
              console.warn(`[UploadQueueStore] Node detached during retry delay: ${fileId}`);
              return;
            }

            // Reset to pending to allow worker to pick it up again, on a
            // fresh path — the failed attempt may have left an object at the
            // old one, and uploads use upsert:false.
            regenerateFilePath(queuedFile);
            queuedFile.status = "pending";
          } else {
            // Permanently failed
            queuedFile.status = "failed";
            queuedFile.error = errorMsg;
            queuedFile.completedAt = new Date().toISOString();

            // NOTE: Do NOT delete file handle here - preserve it for retryFailed()
            // File handles are only cleaned up when:
            // - Upload succeeds
            // - User explicitly clears failed files via clearCompleted() or reset()
          }
          return; // Exit the auth retry loop for non-auth errors
        }
      }
    });

    /**
     * Worker function that processes files from the queue.
     * Note: activeWorkers is incremented before this is called (in spawnWorkerIfNeeded)
     * to ensure accurate concurrency tracking.
     */
    const runWorker = flow(function* runWorker() {
      try {
        while (!self.shouldStop) {
          const file = getNextPendingFile();
          if (!file) {
            // No more work to do
            break;
          }

          yield processFile(file.id);
        }
      } finally {
        self.activeWorkers -= 1;

        // Check if there's more work and capacity to spawn another worker
        // This handles the case where files were added while processing
        // Note: Check queue directly instead of hasWorkToDo to ensure we get fresh state
        const hasPendingWork = self.queue.some((f) => f.status === "pending");
        if (!self.shouldStop && hasPendingWork && self.activeWorkers < self.concurrency) {
          // Spawn another worker to handle remaining work
          // Don't await - let it run independently
          void spawnWorkerIfNeeded();
        }

        // If this was the last worker and no more work, mark processing as complete
        // pending-only: a "retrying" file's owner is still inside processFile,
        // so activeWorkers > 0 keeps isProcessing true until it resolves.
        const hasWork = self.queue.some((f) => f.status === "pending");
        if (self.activeWorkers === 0 && !hasWork) {
          self.isProcessing = false;
        }
      }
    });

    /**
     * Spawn a worker if there's work to do and capacity available.
     * Increments activeWorkers synchronously before starting the async work.
     */
    function spawnWorkerIfNeeded(): void {
      if (self.shouldStop || !self.hasWorkToDo || self.activeWorkers >= self.concurrency) {
        return;
      }
      self.activeWorkers += 1;
      // Start worker - don't await, let it run independently
      void runWorker();
    }

    return {
      /**
       * Add files to the upload queue with pre-upload validation.
       * Validates files before adding: skips 0-byte files and unreadable files.
       * Computes SHA256 content hash for versioning/duplicate detection.
       * Returns a summary of added vs skipped files for user feedback.
       *
       * @param files - Array of File objects to upload
       * @param config - Configuration including buildingId, orgId, and optional projectId
       * @returns Summary of added and skipped files
       */
      addFiles: flow(function* addFiles(
        files: File[],
        config: AddFilesConfig
      ): Generator<Promise<unknown>, AddFilesSummary, unknown> {
        const numericBuildingId = parseBuildingStorageId(config.buildingId);
        if (numericBuildingId === null) {
          throw new Error("Invalid building ID: expected a positive safe integer");
        }
        const batchId = uuidv4();
        const skippedFiles: SkippedFile[] = [];
        const blockedUploads: QueuedFileSnapshotIn[] = [];

        const validFiles: File[] = [];
        let emptyCount = 0;
        let tooLargeCount = 0;
        let unreadableCount = 0;
        let unsupportedTypeCount = 0;
        const pendingUploads: Array<{ file: File; snapshot: QueuedFileSnapshotIn }> = [];

        // Phase 1: Validate all files
        for (const file of files) {
          // Silently drop OS junk files (.DS_Store, Thumbs.db, etc.)
          if (isIgnoredFile(file)) continue;

          // Check 1: Skip 0-byte files
          if (file.size === 0) {
            skippedFiles.push({
              fileName: file.name,
              fileSize: 0,
              reason: "empty",
              reasonDescription: "File is empty (0 bytes)",
            });
            emptyCount++;
            continue;
          }

          // Check 2: Skip files exceeding 500MB limit
          if (file.size > MAX_FILE_SIZE) {
            skippedFiles.push({
              fileName: file.name,
              fileSize: file.size,
              reason: "too-large",
              reasonDescription: `File exceeds 500MB limit (${(file.size / (1024 * 1024)).toFixed(0)}MB)`,
            });
            tooLargeCount++;
            continue;
          }

          // Check 3: Verify file handle is readable
          try {
            const slice = file.slice(0, 1);
            yield slice.arrayBuffer();
          } catch {
            skippedFiles.push({
              fileName: file.name,
              fileSize: file.size,
              reason: "unreadable",
              reasonDescription: "File is not readable",
            });
            unreadableCount++;
            continue;
          }

          // Check 4: Blocked file types go directly to queue as failed + non-retryable
          // This routes them through FailedUploadsPanel instead of the legacy red toast
          if (isBlockedFileType(file)) {
            const t = getTranslationsStatic();
            const id = uuidv4();
            blockedUploads.push({
              id,
              batchId,
              fileName: file.name,
              fileSize: file.size,
              filePath: "",
              relativePath: null,
              status: "failed",
              retryCount: 0,
              error: t.documents.uploadErrors.unsupportedFileType,
              progress: 0,
              contentType: resolveContentType(file),
              addedAt: new Date().toISOString(),
              startedAt: null,
              completedAt: new Date().toISOString(),
              buildingId: config.buildingId,
              orgId: config.orgId,
              projectId: config.projectId ?? null,
              parentFolderId: config.parentFolderId ?? null,
              contentHash: null,
              embeddedCreatedAt: null,
              embeddedModifiedAt: null,
              retryable: false,
            });
            unsupportedTypeCount++;
            continue;
          }

          validFiles.push(file);
        }

        // Phase 2: Add valid files to queue (with hash computation)
        for (const file of validFiles) {
          const id = uuidv4();
          const dotIndex = file.name.lastIndexOf(".");
          const fileExt = dotIndex > 0 ? file.name.slice(dotIndex + 1) : "";
          const uniqueFileName = fileExt ? `${uuidv4()}.${fileExt}` : uuidv4();
          const filePath = `${config.orgId}/${config.buildingId}/${uniqueFileName}`;

          // Get relative path if available (from drag-drop with folders)
          // webkitRelativePath is a non-standard property available on File objects from directory input
          const relativePath =
            "webkitRelativePath" in file && typeof file.webkitRelativePath === "string"
              ? file.webkitRelativePath || null
              : null;

          // Compute content hash for versioning (null on failure)
          let contentHash: string | null = null;
          try {
            contentHash = (yield computeFileHash(file)) as string | null;
          } catch (hashError) {
            console.warn("[UploadQueueStore] Failed to compute hash for:", file.name, hashError);
            // Continue without hash - versioning will use path-based detection only
          }

          // Extract embedded dates from file metadata (EXIF, PDF, Office XML)
          // Serialize to JSON string for MST flow generator compatibility
          let embeddedCreatedAt: string | null = null;
          let embeddedModifiedAt: string | null = null;
          try {
            const datesJson = (yield extractEmbeddedDates(file).then((d) =>
              JSON.stringify(d)
            )) as string;
            const dates = JSON.parse(datesJson) as EmbeddedFileDates;
            embeddedCreatedAt = dates.original_created_at;
            embeddedModifiedAt = dates.document_content_modified_date;
          } catch (dateError) {
            console.warn("[UploadQueueStore] Failed to extract dates for:", file.name, dateError);
          }

          // Hold the selection until the server admits the entire batch.
          pendingUploads.push({
            file,
            snapshot: {
              id,
              batchId,
              fileName: file.name,
              fileSize: file.size,
              filePath,
              relativePath,
              status: "pending",
              retryCount: 0,
              error: null,
              progress: 0,
              contentType: resolveContentType(file),
              addedAt: new Date().toISOString(),
              startedAt: null,
              completedAt: null,
              // Per-file config prevents race condition when adding files from different projects
              buildingId: config.buildingId,
              orgId: config.orgId,
              projectId: config.projectId ?? null,
              // Parent folder ID for uploads into a selected folder
              parentFolderId: config.parentFolderId ?? null,
              // Content hash for versioning duplicate detection
              contentHash,
              // Embedded dates from file metadata (EXIF, PDF, Office XML)
              embeddedCreatedAt,
              embeddedModifiedAt,
            },
          });
        }

        if (pendingUploads.length > 0) {
          const reservations = (yield reserveBuildingStorageUploads(
            numericBuildingId,
            pendingUploads.map(({ snapshot }) => ({
              idempotencyKey: snapshot.id,
              path: snapshot.filePath,
              size: snapshot.fileSize,
            }))
          )) as Awaited<ReturnType<typeof reserveBuildingStorageUploads>>;
          let byId: Map<string, StorageReservation>;
          try {
            byId = checkedQueueReservations(pendingUploads, reservations);
          } catch (error) {
            yield releaseUnattachedReservations(reservations, numericBuildingId);
            throw error;
          }
          for (const { file, snapshot } of pendingUploads) {
            const reservation = byId.get(snapshot.id);
            if (!reservation) throw new Error("Storage reservation missing from batch response");
            self.fileHandles.set(snapshot.id, file);
            self.queue.push(
              QueuedFile.create({
                ...snapshot,
                reservationId: reservation.reservationId,
                reservationExpiresAt: reservation.expiresAt ?? null,
              })
            );
          }
        }
        self.skippedFiles = skippedFiles;
        for (const snapshot of blockedUploads) self.queue.push(QueuedFile.create(snapshot));

        // Return summary for UI feedback
        return {
          added: validFiles.length,
          skipped: skippedFiles.length,
          skippedByReason: {
            empty: emptyCount,
            too_large: tooLargeCount,
            unreadable: unreadableCount,
            unsupported_type: unsupportedTypeCount,
          },
        };
      }),

      /**
       * Start processing the upload queue.
       * Spawns workers up to the concurrency limit.
       * Workers will continue processing until all files are done or cancelled.
       */
      startProcessing() {
        console.log(
          "[UploadQueueStore] startProcessing called. isProcessing:",
          self.isProcessing,
          "hasWorkToDo:",
          self.hasWorkToDo,
          "queue:",
          self.queue.length
        );
        if (self.isProcessing) return;
        if (!self.hasWorkToDo) return;

        self.isProcessing = true;
        self.shouldStop = false;

        // Spawn workers up to concurrency limit
        // Each worker will process files until none remain
        while (self.activeWorkers < self.concurrency && self.hasWorkToDo) {
          spawnWorkerIfNeeded();
        }
      },

      /**
       * @deprecated Use startProcessing() instead for non-blocking queue processing.
       * This flow-based version is kept for backwards compatibility.
       * Spawns workers and waits for all to complete.
       */
      processQueue: flow(function* processQueue() {
        if (self.isProcessing) return;
        if (!self.hasWorkToDo) return;

        self.isProcessing = true;
        self.shouldStop = false;

        try {
          // Spawn workers up to concurrency limit
          // Increment activeWorkers synchronously to ensure accurate counting
          const workers: Promise<void>[] = [];
          while (self.activeWorkers < self.concurrency && self.hasWorkToDo) {
            self.activeWorkers += 1;
            workers.push(runWorker() as unknown as Promise<void>);
          }

          // Wait for all initially spawned workers to complete
          // Note: Workers may spawn additional workers as they complete
          yield Promise.all(workers);

          // Wait for any additional workers that were spawned
          while (self.activeWorkers > 0) {
            yield new Promise((resolve) => setTimeout(resolve, 100));
          }
        } finally {
          self.isProcessing = false;
        }
      }),

      /**
       * Retry all failed files.
       * Resets failed items to pending status for reprocessing.
       * File handles are preserved on failure specifically to enable this retry.
       */
      retryFailed() {
        const failedFiles = self.queue.filter((f) => f.status === "failed");
        // One explicit Retry click is a new completion batch; automatic retries keep theirs.
        const retryBatchId = uuidv4();
        let retriedCount = 0;
        let skippedCount = 0;

        for (const file of failedFiles) {
          // Skip non-retryable files (e.g. unsupported file types)
          if (!file.retryable) {
            continue;
          }

          releaseHeldReservations([file]);
          // Only retry if we still have the file handle
          if (self.fileHandles.has(file.id)) {
            // Fresh path for the same reason as the auto-retry: the failed
            // attempt's object may still occupy the old one.
            regenerateFilePath(file);
            file.batchId = retryBatchId;
            file.status = "pending";
            file.retryCount = 0;
            file.error = null;
            file.progress = 0;
            file.startedAt = null;
            file.completedAt = null;
            retriedCount++;
          } else {
            // This shouldn't happen now that we preserve file handles on failure,
            // but log it if it does for debugging
            console.warn(`[UploadQueueStore] Cannot retry ${file.fileName} - file handle missing`);
            skippedCount++;
          }
        }

        if (retriedCount > 0 || skippedCount > 0) {
          console.log(
            `[UploadQueueStore] retryFailed: ${retriedCount} files reset to pending, ${skippedCount} skipped`
          );
        }
      },

      /**
       * Clear all failed files from the queue.
       * Also cleans up their file handles.
       */
      clearFailed() {
        const failedFiles = self.queue.filter(
          (f) => f.status === "failed" && !f.registrationUncertain
        );
        releaseHeldReservations(failedFiles);
        for (const file of failedFiles) {
          self.fileHandles.delete(file.id);
          self.queue.remove(file);
        }
      },

      /**
       * Clear completed files from the queue.
       */
      clearCompleted() {
        deferCleanupUntilRegistration(
          self.queue.filter((f) => f.status === "complete" && !f.documentCreated)
        );
        const completed = self.queue.filter((f) => f.status === "complete" && f.documentCreated);
        releaseHeldReservations(completed);
        for (const file of completed) {
          self.fileHandles.delete(file.id);
          self.queue.remove(file);
        }
      },

      /**
       * Cancel all pending uploads and clear the queue.
       */
      cancelAll() {
        self.shouldStop = true;
        self.isProcessing = false;
        deferCleanupUntilRegistration(
          self.queue.filter((f) => f.status === "complete" && !f.documentCreated)
        );
        const removable = self.queue.filter(
          (f) => !f.registrationUncertain && (f.status !== "complete" || f.documentCreated)
        );
        releaseHeldReservations(removable);

        // Clear all file handles
        for (const file of removable) self.fileHandles.delete(file.id);

        for (const file of removable) self.queue.remove(file);

        // Clear skipped files
        self.skippedFiles = [];
      },

      /**
       * Get the file handle for a queued file.
       * Used by upload processor for actual upload.
       */
      getFileHandle(id: string): File | undefined {
        return self.fileHandles.get(id);
      },

      /**
       * Clear the entire queue and reset state.
       */
      reset() {
        self.shouldStop = true;
        self.isProcessing = false;
        deferCleanupUntilRegistration(
          self.queue.filter((f) => f.status === "complete" && !f.documentCreated)
        );
        const removable = self.queue.filter(
          (f) => !f.registrationUncertain && (f.status !== "complete" || f.documentCreated)
        );
        releaseHeldReservations(removable);
        for (const file of removable) self.fileHandles.delete(file.id);
        for (const file of removable) self.queue.remove(file);
        self.skippedFiles = [];
        self.activeWorkers = 0;
      },

      /** Remove a dismissed row only after its document RPC reached a terminal state. */
      finalizeDismissed(fileId: string) {
        if (!self.pendingRegistrationCleanup.has(fileId)) return;
        const file = self.queue.find((queued) => queued.id === fileId);
        if (!file) {
          self.pendingRegistrationCleanup.delete(fileId);
          return;
        }
        if (file.registrationUncertain) return;
        if (!file.documentCreated && file.status !== "failed") return;
        self.pendingRegistrationCleanup.delete(fileId);
        releaseHeldReservations([file]);
        self.fileHandles.delete(fileId);
        self.queue.remove(file);
      },

      /**
       * Clear skipped files list.
       * Call this if you want to clear skipped file history without resetting the queue.
       */
      clearSkippedFiles() {
        self.skippedFiles = [];
      },
    };
  });

export type UploadQueueStoreInstance = Instance<typeof UploadQueueStore>;
export type UploadQueueStoreSnapshotIn = SnapshotIn<typeof UploadQueueStore>;
export type UploadQueueStoreSnapshotOut = SnapshotOut<typeof UploadQueueStore>;
